import { useMemo, useState } from 'react';

import {
  Box,
  FormControl,
  FormLabel,
  Icon,
  Select,
  Skeleton,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@ecoinvent/ui';
import { FiLock } from 'react-icons/fi';

import useAllIndicators from 'hooks/data/datasets/lcia/useAllIndicators';

const DEFAULT_METHOD = 'IPCC 2021';

const GuestImpactAssessmentDetails = () => {
  const { data: indicatorData, isLoading } = useAllIndicators();

  const methods = useMemo(() => (indicatorData ? Object.keys(indicatorData) : []), [indicatorData]);

  const [selectedMethod, setSelectedMethod] = useState<string>('');

  const effectiveMethod = selectedMethod || methods.find((m) => m === DEFAULT_METHOD) || methods[0] || '';

  const rows = useMemo(() => {
    if (!indicatorData || !effectiveMethod) return [];
    return Object.entries(indicatorData[effectiveMethod]).flatMap(([category, indicators]) =>
      Object.keys(indicators).map((indicator) => ({ category, indicator }))
    );
  }, [indicatorData, effectiveMethod]);

  return (
    <Stack spacing={4}>
      <FormControl size="sm">
        <Skeleton isLoaded={!isLoading} alignSelf="flex-start">
          <FormLabel fontSize="sm">LCIA Method</FormLabel>
        </Skeleton>
        <Skeleton isLoaded={!isLoading}>
          <Select isDisabled={!methods.length} value={effectiveMethod} onChange={(e) => setSelectedMethod(e.target.value)}>
            {methods.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </Skeleton>
      </FormControl>
      <Skeleton isLoaded={!isLoading}>
        <TableContainer whiteSpace="break-spaces">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th pl={4}>Impact category</Th>
                <Th>Indicator</Th>
                <Th isNumeric whiteSpace={'nowrap'}>
                  Impact Score
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map(({ category, indicator }, i) => (
                <Tr key={i}>
                  <Td pl={4}>{category}</Td>
                  <Td>{indicator}</Td>
                  <Td isNumeric>
                    <Tooltip hasArrow label="Upgrade to see impact scores" placement="top">
                      <Box as="span" display="inline-block">
                        <Icon as={FiLock} color="gray.400" />
                      </Box>
                    </Tooltip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Skeleton>
    </Stack>
  );
};

export default GuestImpactAssessmentDetails;
